import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Gift, Star, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { vehicleSettingsAPI } from '../../lib/api';

// The buyer-facing switches a manager actually needs mid-shift, surfaced where
// the manager already is. Same endpoint as Showroom Settings — one source of
// truth, so the two screens can never drift.
const TOGGLES = [
    ['welcome_screen_enabled', 'Welcome screen', 'A one-time hello the first time a buyer taps any of your cars.'],
    ['fi_financing_enabled', 'Financing', ''],
    ['fi_warranty_enabled', 'Extended warranty', ''],
    ['fi_insurance_enabled', 'Insurance quotes', ''],
    ['fi_trade_in_enabled', 'Trade-in appraisals', ''],
];

export default function CCBuyerExperiencePanel({ organizationId }) {
    const { t } = useTranslation();
    const [s, setS] = useState(null);
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        if (!organizationId) return;
        try {
            const r = await vehicleSettingsAPI.get(organizationId);
            setS(r.data || {});
        } catch { setS({}); }
    }, [organizationId]);

    useEffect(() => { load(); }, [load]);

    const set = (k, v) => setS((p) => ({ ...p, [k]: v }));

    const save = async () => {
        setBusy(true);
        try {
            await vehicleSettingsAPI.update(organizationId, {
                buyer_theme: s.buyer_theme || 'dark',
                welcome_screen_enabled: !!s.welcome_screen_enabled,
                fi_financing_enabled: !!s.fi_financing_enabled,
                fi_warranty_enabled: !!s.fi_warranty_enabled,
                fi_insurance_enabled: !!s.fi_insurance_enabled,
                fi_trade_in_enabled: !!s.fi_trade_in_enabled,
                test_drive_gift_enabled: !!s.test_drive_gift_enabled,
                test_drive_gift_title: s.test_drive_gift_title || null,
                test_drive_gift_value: Number(s.test_drive_gift_value) || null,
                test_drive_gift_expiry_days: Number(s.test_drive_gift_expiry_days) || null,
                review_request_enabled: !!s.review_request_enabled,
                welcome_title: s.welcome_title || '',
                welcome_body: s.welcome_body || '',
                welcome_signature_name: s.welcome_signature_name || '',
                welcome_signature_title: s.welcome_signature_title || '',
            });
            toast.success(t('offerDeposit.settingsSaved', 'Showroom settings saved'));
            load();
        } catch (err) { toast.error(err.response?.data?.detail || t('common.error', 'Something went wrong')); }
        finally { setBusy(false); }
    };

    if (!s) return <p className="text-sm text-muted-foreground" data-testid="cc-buyer-exp-loading">{t('common.loading', 'Loading…')}</p>;

    return (
        <div className="space-y-5" data-testid="cc-buyer-experience">
            <div className="space-y-2">
                <Label>{t('buyerTheme.label', 'Buyer page appearance')}</Label>
                <div className="grid grid-cols-2 gap-2 max-w-sm">
                    {[['dark', t('buyerTheme.dark', 'Night (dark)')], ['light', t('buyerTheme.light', 'Day (light)')]].map(([key, label]) => (
                        <button key={key} type="button" onClick={() => set('buyer_theme', key)}
                            data-testid={`cc-buyer-theme-${key}`}
                            className={`h-11 rounded-lg border text-sm font-semibold transition-colors ${(s.buyer_theme || 'dark') === key ? 'border-primary bg-primary/10 text-foreground' : 'border-border bg-background text-muted-foreground hover:bg-accent'}`}>
                            {label}
                        </button>
                    ))}
                </div>
            </div>

            {/* 9B — the gift exists only after a completed test drive. */}
            <div className="rounded-xl border border-border p-3 space-y-3" data-testid="cc-test-drive-gift">
                <label className="flex items-center justify-between gap-3 cursor-pointer">
                    <span className="flex items-center gap-2 text-sm font-semibold">
                        <Gift className="w-4 h-4 text-amber-500" />
                        {t('gift.toggleTitle', 'Test Drive Gift')}
                    </span>
                    <Switch checked={!!s.test_drive_gift_enabled}
                        onCheckedChange={(c) => set('test_drive_gift_enabled', c)}
                        data-testid="cc-gift-enabled" />
                </label>
                <p className="text-xs text-muted-foreground">
                    {t('gift.toggleHint', 'Sent only after a salesperson marks the test drive complete — never before, and never tied to leaving a review.')}
                </p>
                {s.test_drive_gift_enabled && (
                    <div className="grid gap-2 sm:grid-cols-3">
                        <div className="space-y-1">
                            <Label className="text-xs">{t('gift.fieldTitle', 'Gift name')}</Label>
                            <Input value={s.test_drive_gift_title || ''} placeholder="$25 Gas Card"
                                onChange={(e) => set('test_drive_gift_title', e.target.value)}
                                data-testid="cc-gift-title" />
                        </div>
                        <div className="space-y-1">
                            <Label className="text-xs">{t('gift.fieldValue', 'Value')}</Label>
                            <Input type="number" min="1" max="500" value={s.test_drive_gift_value ?? ''}
                                onChange={(e) => set('test_drive_gift_value', e.target.value)}
                                data-testid="cc-gift-value" />
                        </div>
                        <div className="space-y-1">
                            <Label className="text-xs">{t('gift.fieldExpiry', 'Expires in (days)')}</Label>
                            <Input type="number" min="1" max="365" value={s.test_drive_gift_expiry_days ?? ''}
                                onChange={(e) => set('test_drive_gift_expiry_days', e.target.value)}
                                data-testid="cc-gift-expiry" />
                        </div>
                    </div>
                )}
            </div>

            {/* 9C — a SEPARATE feature from the gift above: different trigger,
                different message, and it never mentions a gift. */}
            <div className="rounded-xl border border-border p-3 space-y-2" data-testid="cc-review-requests">
                <label className="flex items-center justify-between gap-3 cursor-pointer">
                    <span className="flex items-center gap-2 text-sm font-semibold">
                        <Star className="w-4 h-4 text-sky-500" />
                        {t('reviewReq.toggleTitle', 'Review requests')}
                    </span>
                    <Switch checked={!!s.review_request_enabled}
                        onCheckedChange={(c) => set('review_request_enabled', c)}
                        data-testid="cc-review-enabled" />
                </label>
                <p className="text-xs text-muted-foreground">
                    {t('reviewReq.toggleHint', 'Asked twice, never more: after a completed test drive, or after {{hours}} hours of silence from a buyer you already spoke with. One ask per buyer per month.', { hours: s.review_request_cold_hours || 72 })}
                </p>
                {s.review_request_enabled && !s.review_request_destination && (
                    <p className="flex items-start gap-2 text-xs text-amber-600 dark:text-amber-400" data-testid="cc-review-no-destination">
                        <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                        {t('reviewReq.noDestination', 'Nothing will be sent yet: this dealership has no Review Shield card, and every ask goes through it so an unhappy buyer stays private.')}
                    </p>
                )}
                {s.review_request_enabled && s.review_request_destination && (
                    <p className="text-xs text-muted-foreground" data-testid="cc-review-destination">
                        {t('reviewReq.destination', 'Buyers are sent to your Review Shield page ({{path}}), which keeps unhappy feedback private.', { path: s.review_request_destination })}
                    </p>
                )}
            </div>

            <div className="space-y-2">
                {TOGGLES.map(([key, label, hint]) => (
                    <label key={key} className="flex items-center justify-between gap-3 rounded-lg border border-border px-3 py-2.5 cursor-pointer">
                        <span>
                            <span className="text-sm block">{t(`ccBuyerExp.${key}`, label)}</span>
                            {hint ? <span className="text-xs text-muted-foreground">{t(`ccBuyerExp.${key}_hint`, hint)}</span> : null}
                        </span>
                        <Switch checked={!!s[key]} onCheckedChange={(c) => set(key, c)}
                            data-testid={`cc-buyer-exp-${key.replace(/_/g, '-')}`} />
                    </label>
                ))}
            </div>

            {/* The welcome screen used to say the same thing for every
                dealership. These four fields are per-organization overrides —
                each one blank falls back to the shipped trilingual default. */}
            {!!s.welcome_screen_enabled && (
                <div className="rounded-xl border border-border p-3 space-y-3" data-testid="cc-welcome-copy">
                    <p className="text-sm font-semibold">{t('welcomeCopy.title', 'Welcome screen message')}</p>
                    <p className="text-xs text-muted-foreground">
                        {t('welcomeCopy.hint', 'Leave a field empty to use the default wording in the buyer’s own language.')}
                    </p>
                    <div className="space-y-1">
                        <Label className="text-xs">{t('welcomeCopy.fieldTitle', 'Headline')}</Label>
                        <Input value={s.welcome_title || ''} maxLength={120}
                            placeholder={t('welcomeScreen.title', 'We built this to make finding your next vehicle easy.')}
                            onChange={(e) => set('welcome_title', e.target.value)}
                            data-testid="cc-welcome-title" />
                    </div>
                    <div className="space-y-1">
                        <Label className="text-xs">{t('welcomeCopy.fieldBody', 'Message')}</Label>
                        <Textarea value={s.welcome_body || ''} maxLength={400} rows={3}
                            placeholder={t('welcomeScreen.body', "We'd love your feedback at the end.")}
                            onChange={(e) => set('welcome_body', e.target.value)}
                            data-testid="cc-welcome-body" />
                    </div>
                    <div className="grid gap-2 sm:grid-cols-2">
                        <div className="space-y-1">
                            <Label className="text-xs">{t('welcomeCopy.fieldSignName', 'Signed by (name)')}</Label>
                            <Input value={s.welcome_signature_name || ''} maxLength={60}
                                placeholder="Marc Tremblay"
                                onChange={(e) => set('welcome_signature_name', e.target.value)}
                                data-testid="cc-welcome-signature-name" />
                        </div>
                        <div className="space-y-1">
                            <Label className="text-xs">{t('welcomeCopy.fieldSignTitle', 'Their title')}</Label>
                            <Input value={s.welcome_signature_title || ''} maxLength={60}
                                placeholder={t('welcomeCopy.signTitlePlaceholder', 'General Manager')}
                                onChange={(e) => set('welcome_signature_title', e.target.value)}
                                data-testid="cc-welcome-signature-title" />
                        </div>
                    </div>
                </div>
            )}

            <Button onClick={save} disabled={busy} data-testid="cc-buyer-exp-save">
                {busy ? t('common.saving', 'Saving…') : t('offerDeposit.settingsSave', 'Save settings')}
            </Button>
        </div>
    );
}
